import React from 'react';
import PropTypes from 'prop-types';
import { Card } from '../ui/BaseComponents';
import FileUploadSection from './FileUploadSection';
import QuickSettings from './QuickSettings';

/**
 * LLMGenerateTab - Tab for generating questions with LLM
 * Clean design without emoticons, following the V2 design system
 */
function LLMGenerateTab({
  // File Upload
  llmProvider,
  uploadedFiles,
  fileContents,
  onFileUpload,
  onRemoveFile,
  formatFileSize,
  
  // Generation Settings
  numberOfQuestions,
  setNumberOfQuestions,
  difficulty,
  setDifficulty,
  includeCodeBlocks,
  setIncludeCodeBlocks,
  multipleChoiceMode,
  setMultipleChoiceMode,
  appendToExisting,
  setAppendToExisting,
  existingQuestionsCount = 0,
  userGuidance,
  setUserGuidance,
  topicTags,
  currentTag,
  setCurrentTag,
  onAddTopicTag,
  onRemoveTopicTag,
  
  // PDF Processing Mode (triple-switch: 'local', 'native', 'images')
  pdfProcessingMode,
  setPdfProcessingMode,
  imageQuality,
  setImageQuality,
  hasPdfFiles,
  supportsVision,
  
  // Template
  selectedTemplateId,
  onTemplateSelect,
  
  // Generation
  isGenerating,
  generationLog,
  apiKey,
  onGenerate
}) {
  // Allow generation even without content - user might just want to generate based on tags or template
  // Clipboard mode doesn't need an API key (prompts are copied for external use)
  const canGenerate = apiKey || llmProvider === 'custom' || llmProvider === 'clipboard';
  
  // Check if there's any context for the LLM
  const hasContent = fileContents && fileContents.length > 0;
  const hasGuidance = userGuidance && userGuidance.trim().length > 0;
  const hasTags = topicTags && topicTags.length > 0;
  const hasContext = hasContent || hasGuidance || hasTags;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column - Configuration */}
      <div className="space-y-4">
        {/* File Upload */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Quellmaterial
          </h3>
          <FileUploadSection
            llmProvider={llmProvider}
            pdfProcessingMode={pdfProcessingMode}
            setPdfProcessingMode={setPdfProcessingMode}
            imageQuality={imageQuality}
            setImageQuality={setImageQuality}
            hasPdfFiles={hasPdfFiles}
            supportsVision={supportsVision}
            uploadedFiles={uploadedFiles}
            fileContents={fileContents}
            onFileUpload={onFileUpload}
            onRemoveFile={onRemoveFile}
            formatFileSize={formatFileSize}
          />
        </Card>

        {/* Quick Settings */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Einstellungen
          </h3>
          <QuickSettings
            numberOfQuestions={numberOfQuestions}
            setNumberOfQuestions={setNumberOfQuestions}
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            includeCodeBlocks={includeCodeBlocks}
            setIncludeCodeBlocks={setIncludeCodeBlocks}
            multipleChoiceMode={multipleChoiceMode}
            setMultipleChoiceMode={setMultipleChoiceMode}
            userGuidance={userGuidance}
            setUserGuidance={setUserGuidance}
            topicTags={topicTags}
            currentTag={currentTag}
            setCurrentTag={setCurrentTag}
            onAddTopicTag={onAddTopicTag}
            onRemoveTopicTag={onRemoveTopicTag}
          />
        </Card>

        {/* Generate Button */}
        <Card padding="sm">
          {/* Append to existing checkbox - only show when there are existing questions */}
          {existingQuestionsCount > 0 && (
            <div className="mb-3 p-3 bg-secondary-50 dark:bg-secondary-900/20 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={appendToExisting}
                  onChange={(e) => setAppendToExisting(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-secondary-600 focus:ring-secondary-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Zu bestehenden {existingQuestionsCount} Fragen hinzufügen
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Neue Fragen werden angehängt statt zu ersetzen
                  </p>
                </div>
              </label>
            </div>
          )}
          
          <button
            onClick={() => onGenerate()}
            disabled={isGenerating || !canGenerate}
            className={`
              w-full py-3 px-6 rounded-lg font-semibold transition-all
              ${isGenerating || !canGenerate
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
              }
            `}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generiere...
              </span>
            ) : appendToExisting && existingQuestionsCount > 0 ? (
              <span>+ {numberOfQuestions} weitere Fragen generieren</span>
            ) : (
              <span>{numberOfQuestions} Fragen generieren</span>
            )}
          </button>
          
          {!canGenerate && (
            <p className="text-xs text-center mt-2 text-gray-500 dark:text-gray-400">
              API Key fehlt - siehe Einstellungen Tab
            </p>
          )}
          
          {/* Warning when no context */}
          {canGenerate && !hasContext && (
            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Kein Kontext vorhanden
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Ohne Quellmaterial, Themen-Tags oder Guidance generiert das LLM beliebige Fragen. 
                    Füge mindestens ein Thema unter "Einstellungen" hinzu.
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Right Column - Generation Status */}
      <div className="space-y-4">
        {/* Status Card */}
        <Card className="min-h-[300px] flex flex-col">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Status
          </h3>
          
          {generationLog ? (
            <div className="flex-1">
              <div className="bg-gray-900 rounded-lg p-4 h-full">
                <pre className="text-success-400 text-xs overflow-auto max-h-64 font-mono whitespace-pre-wrap">
                  {generationLog}
                </pre>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-center items-center text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                Bereit zur Generierung
              </h4>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Lade Quellmaterial hoch und starte die Generierung
              </p>
            </div>
          )}
        </Card>

        {/* Supported Formats */}
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Unterstützte Formate:
            </span>
            <div className="flex gap-2">
              {['PDF', 'DOCX', 'TXT', 'MD'].map(format => (
                <span 
                  key={format}
                  className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-300"
                >
                  {format}
                </span>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

LLMGenerateTab.propTypes = {
  // File Upload
  llmProvider: PropTypes.string.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  fileContents: PropTypes.array.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  formatFileSize: PropTypes.func.isRequired,
  
  // Generation Settings
  numberOfQuestions: PropTypes.number.isRequired,
  setNumberOfQuestions: PropTypes.func.isRequired,
  difficulty: PropTypes.string.isRequired,
  setDifficulty: PropTypes.func.isRequired,
  includeCodeBlocks: PropTypes.bool.isRequired,
  setIncludeCodeBlocks: PropTypes.func.isRequired,
  multipleChoiceMode: PropTypes.oneOf(['single', 'multiple', 'mixed']),
  setMultipleChoiceMode: PropTypes.func,
  appendToExisting: PropTypes.bool,
  setAppendToExisting: PropTypes.func,
  existingQuestionsCount: PropTypes.number,
  userGuidance: PropTypes.string,
  setUserGuidance: PropTypes.func,
  topicTags: PropTypes.array.isRequired,
  currentTag: PropTypes.string.isRequired,
  setCurrentTag: PropTypes.func.isRequired,
  onAddTopicTag: PropTypes.func.isRequired,
  onRemoveTopicTag: PropTypes.func.isRequired,
  
  // PDF Processing Mode
  pdfProcessingMode: PropTypes.oneOf(['local', 'native', 'images']),
  setPdfProcessingMode: PropTypes.func,
  imageQuality: PropTypes.oneOf(['low', 'medium', 'high']),
  setImageQuality: PropTypes.func,
  hasPdfFiles: PropTypes.bool,
  supportsVision: PropTypes.bool,
  
  // Template
  selectedTemplateId: PropTypes.string,
  onTemplateSelect: PropTypes.func,
  
  // Generation
  isGenerating: PropTypes.bool.isRequired,
  generationLog: PropTypes.string,
  apiKey: PropTypes.string,
  onGenerate: PropTypes.func.isRequired
};

export default LLMGenerateTab;
